import { useState, useEffect } from 'react'
import { updateCallout, createCallout, deleteCallout } from '../lib/api'
import { logChange } from '../lib/auditLog'

const CALLOUT_TYPES = [
  { key: 'status', label: 'Status Update', bg: '#dcfce7', text: '#15803d', border: '#86efac' },
  { key: 'parts',  label: 'Parts / Talking Point', bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
]

export default function CalloutModal({ projectId, callout, defaultDate, onClose, onSaved, userProfile }) {
  const [form, setForm] = useState({
    project_id: projectId,
    date: defaultDate || '',
    end_date: '',
    text: '',
    type: 'status',
  })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (callout) {
      setForm({
        project_id: callout.project_id,
        date: callout.date,
        end_date: callout.end_date || '',
        text: callout.text,
        type: callout.type,
      })
    }
  }, [callout])

  const save = async () => {
    if (!form.date || !form.text.trim()) return
    setSaving(true)
    try {
      const payload = { ...form, end_date: form.end_date || null }
      if (callout) {
        await updateCallout(callout.id, payload)
        await logChange({
          projectId: payload.project_id, entityType: 'callout', entityId: callout.id,
          entityLabel: callout.text, action: 'updated', oldData: callout, newData: payload,
          userProfile,
        })
      } else {
        const { data: created } = await createCallout(payload)
        await logChange({
          projectId: payload.project_id, entityType: 'callout', entityId: created?.id,
          entityLabel: payload.text, action: 'created', userProfile,
        })
      }
      onSaved()
      onClose()
    } catch (err) {
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteCallout = async () => {
    if (!confirm(`Delete "${callout.text}"?`)) return
    await logChange({
      projectId: callout.project_id, entityType: 'callout', entityId: callout.id,
      entityLabel: callout.text, action: 'deleted', userProfile,
    })
    await deleteCallout(callout.id)
    onSaved()
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">
            {callout ? 'Edit Callout' : 'Add Callout'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">

          {/* Type selector */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Type</label>
            <div className="flex gap-2">
              {CALLOUT_TYPES.map(t => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, type: t.key }))}
                  className="flex-1 py-2 px-3 rounded-lg text-xs font-semibold border-2 transition-all"
                  style={{
                    backgroundColor: form.type === t.key ? t.bg : 'white',
                    color: form.type === t.key ? t.text : '#6b7280',
                    borderColor: form.type === t.key ? t.border : '#e5e7eb',
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
              <input
                type="date"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">End Date <span className="text-gray-400 font-normal">(optional)</span></label>
              <input
                type="date"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                value={form.end_date}
                min={form.date}
                onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
              />
            </div>
          </div>

          {/* Text */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Callout Text</label>
            <textarea
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20 resize-none"
              rows={3}
              placeholder="e.g. Panel Install 50%, Parts ETA May 15..."
              value={form.text}
              onChange={e => setForm(f => ({ ...f, text: e.target.value }))}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
          <div>
            {callout && (
              <button
                onClick={handleDeleteCallout}
                className="text-sm text-red-500 hover:text-red-700 px-4 py-2 rounded-lg hover:bg-red-50 transition-colors"
              >
                Delete
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving || !form.date || !form.text.trim()}
              className="text-sm font-medium bg-brand-red hover:bg-brand-red-dark disabled:opacity-50 text-white px-5 py-2 rounded-lg transition-colors"
            >
              {saving ? 'Saving...' : callout ? 'Save Changes' : 'Add Callout'}
            </button>
          </div>
        </div>

      </div>
    </div>
  )
}
